import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EMAIL, INDIVIDUAL } from '@constants/constants';
import { logout } from './authReducer';

interface SignupState {
    userRole: string;
    email: string;
    name: string;
    password: string;
    deviceUniqueID: string;
    mobileNumber: string;
    telephoneNumber: string;
    address_one: string;
    address_two: string;
    city: string;
    userState: string;
    country: string;
    pincode: string;
    otpType: string;
    showLink: boolean;
    adminCreateIndividual: boolean;
    adminCreateNurse: boolean;
    adminCreateHospital: boolean;
    verified: boolean;
    hospitalUser: boolean;
    hospital_name: string;
    hospital_branch: string;
    hospital_website: string;
    gst_no: string;
    contact_no: string;
    email_id: string;
    old_email: string;
    admin_name: string;
    hospital_id: string;
    logo_name: string;
    logo: File | null;
    logo_url: string;
    logo_type: string;
    user_uuid: string;
    hospital_tenant_uuid: string;
    user_info_uuid: string;
    tenant_uuid: string;
    hospital_uuid: string;
    hospital_info_uuid: string;
    countryList: Country[];
    stateList: State[];
    userTableID: string;
    hospitalUserTableID: string;
    hospitalsTableID: string;
    old_logo_name: string;
    showPassword: boolean;
    fromOnboarding: boolean;
    cognitoUserId: string;
    oldDeviceId: string;
    oldDeviceUuid: string;
    hospitalAddNurse: boolean;
    logoError: boolean;
    hospitalTenantId:string;
    termsAccepted: boolean;
    // stepPage:number
}

interface Country {
    id: string;
    code: string;
    name: string;
}

interface State {
    id: string;
    name: string;
}

const initialState: SignupState = {
    adminCreateIndividual: false,
    adminCreateNurse: false,
    adminCreateHospital: false,
    userRole: '',
    email: '',
    old_email: '',
    name: '',
    password: '',
    deviceUniqueID: '',
    mobileNumber: '',
    telephoneNumber: '',
    address_one: '',
    address_two: '',
    city: '',
    userState: '',
    country: '',
    pincode: '',
    otpType: EMAIL,
    showLink: true,
    verified: false,
    hospitalUser: false,
    hospital_name: '',
    hospital_branch: '',
    hospital_website: '',
    gst_no: '',
    contact_no: '',
    email_id: '',
    admin_name: '',
    hospital_id: '',
    cognitoUserId: '',
    logo_name: '',
    logo: null,
    logo_url: '',
    logo_type: '',
    user_uuid: '',
    user_info_uuid: '',
    tenant_uuid: '',
    hospital_uuid: '',
    hospital_info_uuid: '',
    hospital_tenant_uuid: '',
    countryList: [],
    stateList: [],
    userTableID: '',
    hospitalUserTableID: '',
    hospitalsTableID: '',
    old_logo_name: '',
    showPassword: false,
    fromOnboarding: false,
    oldDeviceId: '',
    oldDeviceUuid: '',
    hospitalAddNurse: false,
    logoError: false,
    hospitalTenantId:'',
    termsAccepted: false,
    // stepPage: 1
};

// interface RootState {
//     signup: SignupState;
//     // add other slices of state as needed
// }

const signupSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setUserRole(state, action: PayloadAction<string>) {
            state.userRole = action.payload;
        },
        setEmail(state, action: PayloadAction<string>) {
            state.email = action.payload;
        },
        setOldEmail(state, action: PayloadAction<string>) {
            state.old_email = action.payload;
        },
        setName(state, action: PayloadAction<string>) {
            state.name = action.payload;
        },
        setPassword(state, action: PayloadAction<string>) {
            state.password = action.payload;
        },
        setDeviceUniqueID(state, action: PayloadAction<string>) {
            state.deviceUniqueID = action.payload;
        },
        setMobileNumber(state, action: PayloadAction<string>) {
            state.mobileNumber = action.payload;
        },
        setHospitalTelephone(state, action: PayloadAction<string>) {
            state.telephoneNumber = action.payload;
        },
        setAddressOne(state, action: PayloadAction<string>) {
            state.address_one = action.payload;
        },
        setAddressTwo(state, action: PayloadAction<string>) {
            state.address_two = action.payload;
        },
        setCity(state, action: PayloadAction<string>) {
            state.city = action.payload;
        },
        setUserState(state, action: PayloadAction<string>) {
            state.userState = action.payload;
        },
        setCountry(state, action: PayloadAction<string>) {
            state.country = action.payload;
        },
        setPincode(state, action: PayloadAction<string>) {
            state.pincode = action.payload;
        },
        setOtpType(state, action: PayloadAction<string>) {
            state.otpType = action.payload;
        },
        setShowLink(state, action: PayloadAction<boolean>) {
            state.showLink = action.payload;
        },
        setHospitalUser(state, action: PayloadAction<boolean>) {
            state.hospitalUser = action.payload;
        },
        setVerified(state, action: PayloadAction<boolean>) {
            state.verified = action.payload;
        },
        setHospitalName(state, action: PayloadAction<string>) {
            state.hospital_name = action.payload;
        },
        setHospitalBranch(state, action: PayloadAction<string>) {
            state.hospital_branch = action.payload;
        },
        setHospitalWebsite(state, action: PayloadAction<string>) {
            state.hospital_website = action.payload;
        },
        setHospitalGST(state, action: PayloadAction<string>) {
            state.gst_no = action.payload;
        },
        setHospitalContact(state, action: PayloadAction<string>) {
            state.contact_no = action.payload;
        },
        setHospitalAdminName(state, action: PayloadAction<string>) {
            state.admin_name = action.payload;
        },
        setHospitalEmail(state, action: PayloadAction<string>) {
            state.email_id = action.payload;
        },
        setHospitalID(state, action: PayloadAction<string>) {
            state.hospital_id = action.payload;
        },
        setCognitoUserId(state, action: PayloadAction<string>) {
            state.cognitoUserId = action.payload;
        },
        setHospitalLogoName(state, action: PayloadAction<string>) {
            state.logo_name = action.payload;
        },
        setHospitalLogo(state, action: PayloadAction<File>) {
            state.logo = action.payload;
        },
        setFileMetadata(state, action: PayloadAction<{ name: string; url: string; type: string }>) {
            state.logo_name = action.payload.name;
            state.logo_url = action.payload.url;
            state.logo_type = action.payload.type;
        },
        setUserUuid(state, action: PayloadAction<string>) {
            state.user_uuid = action.payload;
        },
        setUserInfoUuid(state, action: PayloadAction<string>) {
            state.user_info_uuid = action.payload;
        },
        setTenantUuid(state, action: PayloadAction<string>) {
            state.tenant_uuid = action.payload;
        },
        setHospitalUuid(state, action: PayloadAction<string>) {
            state.hospital_uuid = action.payload;
        },
        setHospitalInfoUuid(state, action: PayloadAction<string>) {
            state.hospital_info_uuid = action.payload;
        },
        setHospitalTenantUuid(state, action: PayloadAction<string>) {
            state.hospital_tenant_uuid = action.payload;
        },
        setAdminCreateIndividual(state, action: PayloadAction<boolean>) {
            state.adminCreateIndividual = action.payload;
        },
        setAdminCreateNurse(state, action: PayloadAction<boolean>) {
            state.adminCreateNurse = action.payload;
        },
        setAdminCreateHospital(state, action: PayloadAction<boolean>) {
            state.adminCreateHospital = action.payload;
        },
        setCountryList(state, action: PayloadAction<Country[]>) {
            state.countryList = action.payload;
        },
        setStateList(state, action: PayloadAction<State[]>) {
            state.stateList = action.payload;
        },
        setUserTableID(state, action: PayloadAction<string>) {
            state.userTableID = action.payload;
        },
        setHospitalUserTableID(state, action: PayloadAction<string>) {
            state.hospitalUserTableID = action.payload;
        },
        setHospitalsTableID(state, action: PayloadAction<string>) {
            state.hospitalsTableID = action.payload;
        },
        setOldLogoName(state, action: PayloadAction<string>) {
            state.old_logo_name = action.payload;
        },
        setShowUpdatePassword(state, action: PayloadAction<boolean>) {
            state.showPassword = action.payload;
        },
        setFromOnboarding(state, action: PayloadAction<boolean>) {
            state.fromOnboarding = action.payload;
        },
        setOldDeviceUuid(state, action: PayloadAction<string>) {
            state.oldDeviceUuid = action.payload;
        },
        setHospitalTenantId(state, action: PayloadAction<string>) {
            state.hospitalTenantId = action.payload;
        },
        setOldDeviceId(state, action: PayloadAction<string>) {
            state.oldDeviceId = action.payload;
        },
        setHospitalAddNurse(state, action: PayloadAction<boolean>) {
            state.hospitalAddNurse = action.payload;
        },
        setLogoError(state, action: PayloadAction<boolean>) {
            state.logoError = action.payload;
        },
        setTermsAccepted(state, action: PayloadAction<boolean>) {
            state.termsAccepted = action.payload;
        },
        // setStepPage(state, action: PayloadAction<number>) {
        //     state.stepPage = action.payload;
        // }
    },
    extraReducers: (builder) => {
        builder.addCase(logout, () => initialState);
      },
});

export const { setEmail, setOldEmail, setHospitalUser,setHospitalTenantId, setHospitalsTableID, setOldLogoName, setLogoError, setCognitoUserId, setHospitalTelephone, setHospitalAddNurse, setOldDeviceUuid, setOldDeviceId, setShowUpdatePassword, setFromOnboarding, setUserState, setHospitalUserTableID, setUserTableID, setCountryList, setStateList, setAdminCreateNurse, setAdminCreateHospital, setAdminCreateIndividual, setFileMetadata, setHospitalTenantUuid, setUserInfoUuid, setUserUuid, setHospitalInfoUuid, setHospitalUuid, setTenantUuid, setHospitalName, setHospitalLogo, setHospitalLogoName, setHospitalID, setHospitalAdminName, setHospitalEmail, setHospitalGST, setHospitalWebsite, setHospitalContact, setHospitalBranch, setPassword, setDeviceUniqueID,setMobileNumber, setVerified, setOtpType, setUserRole, setName, setShowLink, setAddressOne, setAddressTwo, setCity, setCountry, setPincode,setTermsAccepted } = signupSlice.actions;

export default signupSlice.reducer;